import toastr from 'react-hot-toast';
import { useQuery } from 'react-query';
import { fetchWrapper } from 'utils/fetchWrapper';
import { CACHE_AND_STALE_TIME } from 'constants/index';
import {
  generateFilterExpression,
  generateFilter,
  generateIncludeQuery,
  generatePaginationQuery,
  generateSortQuery,
  generateURL,
} from 'utilities/hooks/urlGeneration';
import { SPARK_RESOURCE_TYPES } from 'constants/common';
import type {
  FilterParameters,
  PaginationQuery,
} from 'utilities/hooks/urlGeneration';
import React from 'react';
import { HistoryFilter } from 'components/History';
import { MemoryChatsResponse } from 'types/Memory/MemoryChats';
import { Chat } from 'types/Chats/Chat';

const getUrl = ({
  memoryID,
  paginationQuery,
  startDate,
  endDate,
}: {
  memoryID: string;
  paginationQuery?: PaginationQuery;
  startDate?: string;
  endDate?: string;
}): string => {
  const andFilterConfig: FilterParameters[] = [];
  if (memoryID) {
    andFilterConfig.push({
      type: 'equal',
      key: 'memoryID',
      value: memoryID,
    });
  }

  const queryArray: string[] = [
    generateSortQuery('createdAt', 'Decs'),
    generateIncludeQuery(['chat']),
    ...(!!paginationQuery ? [generatePaginationQuery(paginationQuery)] : []),

    andFilterConfig.length > 1
      ? generateFilterExpression('and', andFilterConfig)
      : generateFilter('memoryID', memoryID),
  ];

  return generateURL(SPARK_RESOURCE_TYPES.MEMORY_CHATS, queryArray);
};

const getMemoryChats = ({
  memoryID,
  paginationQuery,
  startDate,
  endDate,
}: {
  memoryID: string | undefined;

  paginationQuery?: PaginationQuery;
  startDate?: string;
  endDate?: string;
}) => {
  if (!memoryID) {
    Promise.reject('memory ID not  found');
    return;
  }
  return fetchWrapper<MemoryChatsResponse>({
    url: getUrl({
      memoryID,
      paginationQuery,
      startDate,
      endDate,
    }),
    method: 'GET',
  });
};

// call this with or without sparkIDs
export function useMemoryChatSearch({
  memoryID,
  pagination,
  historyDateRangeFilter,
}: {
  memoryID?: string;
  pagination?: PaginationQuery;
  historyDateRangeFilter?: HistoryFilter['dateRange'];
}) {
  const pageNumber = pagination?.pageNumber;
  const startDate = historyDateRangeFilter?.startDate.format('YYYY-MM-DD');
  const endDate = historyDateRangeFilter?.endDate.format('YYYY-MM-DD');

  const response = useQuery(
    ['memoryChats', memoryID, startDate, endDate, pageNumber],
    () =>
      getMemoryChats({
        memoryID,
        paginationQuery: pagination,
        startDate,
        endDate,
      }),
    {
      onError: (error) => {
        if (error instanceof Error) {
          toastr.error(`failed to Fetch Spark Run: ${error.message}`);
        }
      },
      enabled: !!memoryID,
      ...CACHE_AND_STALE_TIME,
    }
  );

  const { data } = response;
  const { data: memoryChats, included: chats } = data || {};

  const chatByMemoryChatID = React.useMemo(() => {
    return memoryChats?.reduce(
      (acc, memoryChat) => {
        const chat = chats?.find(
          (chat) => chat.id === memoryChat.attributes.chatID
        );
        if (chat) {
          acc[memoryChat.id] = chat;
        }
        return acc;
      },
      {} as Record<string, Chat>
    );
  }, [chats, memoryChats]);

  return {
    memoryChats,
    chats,
    chatByMemoryChatID,
    ...response,
  };
}
